import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Download, Mail, Phone, User, Sparkles, Loader2, Pencil, Printer } from "lucide-react";
import { GoogleDocExportButton } from "@/components/restoration/GoogleDocExportButton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import html2pdf from "html2pdf.js";
import { useAuth } from "@/hooks/useAuth";
import { generate, type IncidentSlim } from "./restorationGenerate";

type ActiveTab = "email" | "phone_script" | "student_call";

interface ParentCommunicationProps {
  studentIdentifier: string;
  gradeLevel: string;
  teacherName: string;
  incidentDescription: string;
  studentReflection: string;
  chosenPath: string;
  conversationNotes: string;
  parentContactType: string;
  parentCallNotes: string;
  followUpDate: string;
  initialTab?: ActiveTab;
  onUpdateParentContactType: (type: "" | "student_with_adult" | "adult_alone") => void;
  onUpdateParentCallNotes: (notes: string) => void;
}

const PATH_LABELS: Record<string, string> = {
  one_to_one_conversation: "a one-on-one restorative conversation",
  supported_meeting: "a supported meeting with a mediator present",
  pause_support: "a pause-and-support approach to stabilize first",
  documentation_only: "documentation of the incident",
};

export const ParentCommunication = (props: ParentCommunicationProps) => {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>(props.initialTab ?? "email");
  const [editedEmail, setEditedEmail] = useState<string | null>(null);
  const [editedPhoneScript, setEditedPhoneScript] = useState<string | null>(null);
  const [editedPrepScript, setEditedPrepScript] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingPhone, setIsGeneratingPhone] = useState(false);
  const [isGeneratingPrep, setIsGeneratingPrep] = useState(false);
  const [aiErrorByTab, setAiErrorByTab] = useState<{
    email: string | null;
    phone_script: string | null;
    student_call: string | null;
  }>({ email: null, phone_script: null, student_call: null });
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [showBaselineByTab, setShowBaselineByTab] = useState<{
    email: boolean;
    phone_script: boolean;
    student_call: boolean;
  }>({ email: false, phone_script: false, student_call: false });
  const [aiUpdatedAt, setAiUpdatedAt] = useState<{
    email: string | null;
    phone_script: string | null;
    student_call: string | null;
  }>({ email: null, phone_script: null, student_call: null });
  const autoDraftedRef = useRef<{ email: boolean; phone: boolean; prep: boolean }>({
    email: false,
    phone: false,
    prep: false,
  });
  const contextSigRef = useRef<string>("");

  // Slim incident payload for the restoration-generate edge function. We
  // don't have a full Incident object here — just the props — so we build a
  // subset. Fields we don't have simply drop out of the server prompt.
  const incidentPayload: IncidentSlim = useMemo(() => ({
    student_identifier: props.studentIdentifier,
    grade_level: props.gradeLevel,
    teacher_name: props.teacherName,
    incident_description: props.incidentDescription,
    student_reflection_responses: props.studentReflection,
    chosen_path: props.chosenPath,
    conversation_notes: props.conversationNotes,
    parent_contact_type: props.parentContactType,
    parent_call_notes: props.parentCallNotes,
    follow_up_date: props.followUpDate,
  }), [
    props.studentIdentifier,
    props.gradeLevel,
    props.teacherName,
    props.incidentDescription,
    props.studentReflection,
    props.chosenPath,
    props.conversationNotes,
    props.parentContactType,
    props.parentCallNotes,
    props.followUpDate,
  ]);

  const storageKey = useMemo(() => {
    const userPart = session?.user?.id ?? "anonymous";
    const core = [
      props.studentIdentifier || "student",
      props.gradeLevel || "grade",
      (props.incidentDescription || "").slice(0, 60),
    ].join("|");
    return `rr-parent-ai:${userPart}:${core}`;
  }, [session?.user?.id, props.studentIdentifier, props.gradeLevel, props.incidentDescription]);

  const contextSig = useMemo(() => JSON.stringify({
    student: props.studentIdentifier,
    grade: props.gradeLevel,
    incident: props.incidentDescription,
    notes: props.conversationNotes,
    reflection: props.studentReflection,
    followUp: props.followUpDate,
  }), [
    props.studentIdentifier,
    props.gradeLevel,
    props.incidentDescription,
    props.conversationNotes,
    props.studentReflection,
    props.followUpDate,
  ]);

  const parentEmail = useMemo(() => {
    const pathDesc =
      PATH_LABELS[props.chosenPath] ||
      (props.chosenPath
        ? props.chosenPath.replace(/_/g, " ")
        : "a restorative conversation");
    return [
      `Subject: Following Up — ${props.studentIdentifier || "[Student]"} · ${new Date().toLocaleDateString()}`,
      ``,
      `Dear Parent/Guardian of ${props.studentIdentifier || "[Student]"},`,
      ``,
      `I'm writing to follow up on a situation that occurred today${props.teacherName ? ` in ${props.teacherName}` : ""}.`,
      ``,
      `What happened:`,
      props.incidentDescription || "[Brief description of the incident]",
      ``,
      `What we did:`,
      `We had ${pathDesc} with ${props.studentIdentifier || "your child"}. ${props.conversationNotes ? `During our conversation, ${props.conversationNotes.slice(0, 200)}` : "We focused on understanding what happened and working together on next steps."}`,
      ``,
      `Next steps:`,
      `${props.followUpDate ? `- We have a follow-up check-in scheduled for ${props.followUpDate}.` : "- We will check in with your child within the next few days."}`,
      `- We want to partner with you on this. If there's anything happening at home that might help us understand the full picture, please don't hesitate to share.`,
      ``,
      `Your child showed ${props.studentReflection ? "real thoughtfulness in their reflection" : "willingness to engage in the process"}. We believe in their ability to learn from this.`,
      ``,
      `Please feel free to call me if you'd like to discuss this further.`,
      ``,
      `Sincerely,`,
      `[Your name]`,
      `[Your title]`,
      `[School name]`,
      `[Phone number]`,
    ].join("\n");
  }, [props]);

  const phoneScript = useMemo(() => {
    const pathDesc =
      PATH_LABELS[props.chosenPath] ||
      (props.chosenPath
        ? props.chosenPath.replace(/_/g, " ")
        : "a restorative conversation");
    const gradeLabel = props.gradeLevel || "Not specified";
    return [
      `PARENT PHONE CALL SCRIPT`,
      `For: ${props.studentIdentifier || "Student"} · Grade ${gradeLabel} · ${new Date().toLocaleDateString()}`,
      ``,
      `━━━ INCIDENT SUMMARY (your reference) ━━━`,
      `Student: ${props.studentIdentifier || "[Student]"}`,
      `Grade: ${gradeLabel}`,
      `Teacher/Class: ${props.teacherName || "[Not specified]"}`,
      `What happened: ${props.incidentDescription || "[Not yet described]"}`,
      `Path taken: ${pathDesc}`,
      props.conversationNotes ? `Your notes: ${props.conversationNotes}` : "",
      props.studentReflection ? `Student reflection summary: ${props.studentReflection.slice(0, 200)}` : "",
      `Follow-up: ${props.followUpDate || "To be determined"}`,
      ``,
      `━━━ OPENING (30 seconds) ━━━`,
      `"Hi, this is [your name] calling from [school]. Do you have a few minutes?"`,
      ``,
      `Lead with a strength:`,
      `"Before I describe what happened, I want you to know that ${props.studentIdentifier || "your child"} showed real courage in our conversation today. ${props.studentReflection ? "They were honest and thoughtful in their reflection." : "They were willing to talk things through."}"`,
      ``,
      `━━━ DESCRIBE WHAT HAPPENED (1 minute) ━━━`,
      `Use your own words — here's the key info:`,
      `"Today${props.teacherName ? ` in ${props.teacherName}` : ""}, ${props.incidentDescription || "[describe briefly]"}."`,
      props.conversationNotes ? `"When we talked about it, ${props.conversationNotes.slice(0, 150)}."` : "",
      `"We're treating this as a moment to understand and repair, not just react."`,
      ``,
      `━━━ HEAR THEIR PERSPECTIVE (2 minutes) ━━━`,
      `"Before I share more — is anything going on at home that might be affecting them?"`,
      `"What are you seeing on your end?"`,
      `[Listen. Don't interrupt. Take notes.]`,
      ``,
      `━━━ SHARE NEXT STEPS (1 minute) ━━━`,
      `"Here's what we've done: We had ${pathDesc} with ${props.studentIdentifier || "your child"}."`,
      props.studentReflection ? `"They completed a written reflection where they showed real self-awareness."` : "",
      `${props.followUpDate ? `"Our follow-up check-in is scheduled for ${props.followUpDate}."` : `"We'll check in again within the next few days."`}`,
      `"We want you as a partner in this — not just someone we update after the fact."`,
      ``,
      `━━━ CLOSE ━━━`,
      `"What questions do you have?"`,
      `"What would be most helpful for you to know going forward?"`,
      `"The best way to reach me is ___________."`,
    ].filter(Boolean).join("\n");
  }, [props]);

  // Teacher prep document (adult-facing)
  const teacherPrepScript = useMemo(() => {
    return [
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `TEACHER/ADMIN PREP: Student Call Home`,
      `(This document is for YOU — do not share with the student)`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `Student: ${props.studentIdentifier || "[Student]"} · Grade ${props.gradeLevel || "Not specified"}`,
      `Date: ${new Date().toLocaleDateString()}`,
      ``,
      `INCIDENT RECAP:`,
      props.incidentDescription || "[No description provided]",
      ``,
      `WHAT THE STUDENT SAID IN THEIR REFLECTION:`,
      props.studentReflection || "[Reflection not yet received]",
      ``,
      `━━━ HOW TO PREP THE STUDENT (3 min before the call) ━━━`,
      ``,
      `1. Set the frame:`,
      `   "In a moment, you're going to call your parent/guardian.`,
      `    This isn't punishment — it's accountability. You get to`,
      `    tell your own story before someone else does."`,
      ``,
      `2. Coach them on what to cover:`,
      `   • "What happened today" (the facts, briefly)`,
      `   • "What part you played" (not 'they started it' — your part)`,
      `   • "What happens next" (what the school is doing, what you agreed to)`,
      ``,
      `3. Normalize it:`,
      `   "You don't have to be perfect. Just be honest.`,
      `    That takes courage, and your parent will respect it."`,
      ``,
      `━━━ DURING THE CALL — YOUR MOVES ━━━`,
      ``,
      `If the student freezes:`,
      `→ "Tell them what you told me about what happened."`,
      `→ Don't take over. Pause. Let them find the words.`,
      ``,
      `If the parent gets angry:`,
      `→ "I appreciate you hearing this. Can I add some context?"`,
      `→ "The goal is for your child to take ownership. They're doing that right now."`,
      ``,
      `If the student minimizes:`,
      `→ "Tell them the full picture — including the part about [specific detail]."`,
      `→ "You're doing great. Honesty is hard."`,
      ``,
      `After the call:`,
      `→ "That took real guts. I'm proud of how you handled that."`,
      `→ Name the courage it took, even if the call was rough.`,
    ].join("\n");
  }, [props]);

  const studentCallGuideHTML = useMemo(() => {
    return `<h2>My Call Home Guide</h2>
<p><i>Date: ${new Date().toLocaleDateString()}</i></p>
<p>You're about to call your parent or guardian to tell them what happened today. This isn't a punishment — it's a chance to tell <b>your</b> story, in <b>your</b> words.</p>
<hr>
<h3>What to Say</h3>
<p><b>1. Start:</b></p>
<blockquote>"Hi _____________, something happened at school today and I wanted to tell you myself."</blockquote>
<p><b>2. What Happened:</b></p>
<blockquote>"Today, _______________________________________________________________."</blockquote>
<p><i>(Say what happened in your own words. Keep it short and honest.)</i></p>
<p><b>3. My Part:</b></p>
<blockquote>"What I did was _______________________________________________________."</blockquote>
<p><i>(Be honest about YOUR part — not what someone else did. Just yours.)</i></p>
<p><b>4. What's Happening Now:</b></p>
<blockquote>"I talked with my _________________ and we agreed to __________________________."</blockquote>
${props.followUpDate ? `<p>"I have a follow-up on ${props.followUpDate}."</p>` : ""}
<p><b>5. End:</b></p>
<blockquote>"Is there anything you want to ask me?"</blockquote>
<hr>
<h3>Remember</h3>
<ul>
<li>You don't have to be perfect.</li>
<li>Just be honest.</li>
<li>That takes real courage.</li>
<li>The adult in the room is here to help, not to judge you.</li>
</ul>
<p><b>You've got this.</b></p>`;
  }, [props.followUpDate]);

  const studentCallGuideText = useMemo(() => {
    return [
      `MY CALL HOME GUIDE`,
      `Date: ${new Date().toLocaleDateString()}`,
      ``,
      `You're about to call your parent or guardian to tell them`,
      `what happened today. This isn't a punishment — it's a`,
      `chance to tell YOUR story, in YOUR words.`,
      ``,
      `────────────────────────────────────────`,
      `WHAT TO SAY`,
      `────────────────────────────────────────`,
      ``,
      `1. START:`,
      `   "Hi _____________, something happened at`,
      `    school today and I wanted to tell you myself."`,
      ``,
      `2. WHAT HAPPENED:`,
      `   "Today, ___________________________________________."`,
      `   (Say what happened in your own words.)`,
      ``,
      `3. MY PART:`,
      `   "What I did was ___________________________________."`,
      `   (Be honest about YOUR part — not what someone`,
      `    else did. Just yours.)`,
      ``,
      `4. WHAT'S HAPPENING NOW:`,
      `   "I talked with my _____________ and we agreed to`,
      `    _________________________________________________."`,
      props.followUpDate ? `   "I have a follow-up on ${props.followUpDate}."` : "",
      ``,
      `5. END:`,
      `   "Is there anything you want to ask me?"`,
      ``,
      `────────────────────────────────────────`,
      `REMEMBER`,
      `────────────────────────────────────────`,
      ``,
      `• You don't have to be perfect.`,
      `• Just be honest.`,
      `• That takes real courage.`,
      `• The adult in the room is here to help,`,
      `  not to judge you.`,
      ``,
      `You've got this.`,
    ].filter(Boolean).join("\n");
  }, [props.followUpDate]);

  // AI-drafted parent email. Goes through the restoration-generate edge
  // function (same brain as the rest of the Restoration Room) which returns
  // a structured `{ subject, body }` pair we format into a single editable
  // block for the admin.
  const generateAIEmail = async (opts?: { silent?: boolean }) => {
    setIsGenerating(true);
    setAiErrorByTab((s) => ({ ...s, email: null }));
    try {
      const res = await generate(
        "parent_email",
        { incident: incidentPayload },
        session?.access_token,
      );
      if (!res.ok) {
        setAiErrorByTab((s) => ({ ...s, email: res.error || "AI email draft failed." }));
        if (!opts?.silent) {
          toast({
            title: "Couldn't draft email",
            description: res.error,
            variant: "destructive",
          });
        }
        return;
      }
      if (res.content?.kind === "parent_email") {
        const { subject, body } = res.content.data;
        const formatted = `Subject: ${subject}\n\n${body}\n\nSincerely,\n[Your name]\n[Your title]\n[School name]\n[Phone number]`;
        setEditedEmail(formatted);
        setAiUpdatedAt((s) => ({ ...s, email: new Date().toISOString() }));
        setIsEditing(true);
        setAiErrorByTab((s) => ({ ...s, email: null }));
        if (!opts?.silent) {
          toast({ title: "AI rewrite completed", description: "Email updated from incident notes." });
        }
      }
    } catch (err) {
      console.error("[ParentCommunication] AI email error:", err);
      setAiErrorByTab((s) => ({ ...s, email: "AI email draft failed. Try again." }));
      if (!opts?.silent) {
        toast({ title: "Couldn't draft email", variant: "destructive" });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const generateAIPhoneScript = async (opts?: { silent?: boolean }) => {
    setIsGeneratingPhone(true);
    setAiErrorByTab((s) => ({ ...s, phone_script: null }));
    try {
      const res = await generate(
        "parent_phone_script",
        { incident: incidentPayload },
        session?.access_token,
      );
      if (!res.ok) {
        setAiErrorByTab((s) => ({ ...s, phone_script: res.error || "AI phone script draft failed." }));
        if (!opts?.silent) {
          toast({
            title: "Couldn't draft phone script",
            description: res.error,
            variant: "destructive",
          });
        }
        return;
      }
      if (res.content?.kind === "parent_script") {
        setEditedPhoneScript(res.content.data.script);
        setAiUpdatedAt((s) => ({ ...s, phone_script: new Date().toISOString() }));
        setIsEditingPhone(true);
        setAiErrorByTab((s) => ({ ...s, phone_script: null }));
        if (!opts?.silent) {
          toast({ title: "AI rewrite completed", description: "Phone script updated from incident notes." });
        }
      }
    } catch (err) {
      console.error("[ParentCommunication] AI phone error:", err);
      setAiErrorByTab((s) => ({ ...s, phone_script: "AI phone script draft failed. Try again." }));
      if (!opts?.silent) {
        toast({ title: "Couldn't draft phone script", variant: "destructive" });
      }
    } finally {
      setIsGeneratingPhone(false);
    }
  };

  const generateAIPrepScript = async (opts?: { silent?: boolean }) => {
    setIsGeneratingPrep(true);
    setAiErrorByTab((s) => ({ ...s, student_call: null }));
    try {
      const res = await generate(
        "parent_student_call",
        { incident: incidentPayload },
        session?.access_token,
      );
      if (!res.ok) {
        setAiErrorByTab((s) => ({ ...s, student_call: res.error || "AI prep script draft failed." }));
        if (!opts?.silent) {
          toast({
            title: "Couldn't draft prep script",
            description: res.error,
            variant: "destructive",
          });
        }
        return;
      }
      if (res.content?.kind === "parent_script") {
        setEditedPrepScript(res.content.data.script);
        setAiUpdatedAt((s) => ({ ...s, student_call: new Date().toISOString() }));
        setAiErrorByTab((s) => ({ ...s, student_call: null }));
        if (!opts?.silent) {
          toast({ title: "AI rewrite completed", description: "Prep guide updated from incident notes." });
        }
      }
    } catch (err) {
      console.error("[ParentCommunication] AI prep error:", err);
      setAiErrorByTab((s) => ({ ...s, student_call: "AI prep script draft failed. Try again." }));
      if (!opts?.silent) {
        toast({ title: "Couldn't draft prep script", variant: "destructive" });
      }
    } finally {
      setIsGeneratingPrep(false);
    }
  };

  // Restore persisted AI drafts when reopening an incident.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        email?: string | null;
        phone?: string | null;
        prep?: string | null;
      };
      if (saved.email) setEditedEmail(saved.email);
      if (saved.phone) setEditedPhoneScript(saved.phone);
      if (saved.prep) setEditedPrepScript(saved.prep);
    } catch (e) {
      console.warn("[ParentCommunication] failed to restore drafts", e);
    }
  }, [storageKey]);

  // Persist drafts so they remain available later in the same browser.
  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          email: editedEmail,
          phone: editedPhoneScript,
          prep: editedPrepScript,
        }),
      );
    } catch (e) {
      console.warn("[ParentCommunication] failed to persist drafts", e);
    }
  }, [storageKey, editedEmail, editedPhoneScript, editedPrepScript]);

  // If incident context changes materially, allow auto-drafting again.
  useEffect(() => {
    if (contextSigRef.current === contextSig) return;
    contextSigRef.current = contextSig;
    autoDraftedRef.current = { email: false, phone: false, prep: false };
  }, [contextSig]);

  // AI-first default: auto-generate the active tab on first load/open.
  useEffect(() => {
    if (!session?.access_token) return;
    if (activeTab === "email" && !editedEmail && !isGenerating && !autoDraftedRef.current.email) {
      autoDraftedRef.current.email = true;
      void generateAIEmail({ silent: true });
      return;
    }
    if (activeTab === "phone_script" && !editedPhoneScript && !isGeneratingPhone && !autoDraftedRef.current.phone) {
      autoDraftedRef.current.phone = true;
      void generateAIPhoneScript({ silent: true });
      return;
    }
    if (activeTab === "student_call" && !editedPrepScript && !isGeneratingPrep && !autoDraftedRef.current.prep) {
      autoDraftedRef.current.prep = true;
      void generateAIPrepScript({ silent: true });
    }
  }, [
    activeTab,
    session?.access_token,
    editedEmail,
    editedPhoneScript,
    editedPrepScript,
    isGenerating,
    isGeneratingPhone,
    isGeneratingPrep,
  ]);

  const displayedEmail = editedEmail ?? parentEmail;
  const displayedPhoneScript = editedPhoneScript ?? phoneScript;
  const displayedPrepScript = editedPrepScript ?? teacherPrepScript;
  const effectiveEmail = showBaselineByTab.email ? parentEmail : displayedEmail;
  const effectivePhoneScript = showBaselineByTab.phone_script ? phoneScript : displayedPhoneScript;
  const effectivePrepScript = showBaselineByTab.student_call ? teacherPrepScript : displayedPrepScript;
  const activeHasAiDraft =
    activeTab === "email"
      ? !!editedEmail
      : activeTab === "phone_script"
        ? !!editedPhoneScript
        : !!editedPrepScript;
  const activeUpdatedAt = aiUpdatedAt[activeTab];
  const activeIsGenerating =
    activeTab === "email"
      ? isGenerating
      : activeTab === "phone_script"
        ? isGeneratingPhone
        : isGeneratingPrep;
  const activeError = aiErrorByTab[activeTab];
  const activeSourceLabel = showBaselineByTab[activeTab] ? "Notes-based draft" : "AI rewrite";

  const retryActiveAiDraft = () => {
    if (activeTab === "email") {
      void generateAIEmail();
      return;
    }
    if (activeTab === "phone_script") {
      void generateAIPhoneScript();
      return;
    }
    void generateAIPrepScript();
  };

  const refreshFromCurrentNotes = () => {
    setAiErrorByTab((s) => ({ ...s, [activeTab]: null }));
    setShowBaselineByTab((s) => ({ ...s, [activeTab]: false }));
    retryActiveAiDraft();
  };

  const clearSavedDraftsForIncident = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("[ParentCommunication] failed to clear saved drafts", e);
    }
    setEditedEmail(null);
    setEditedPhoneScript(null);
    setEditedPrepScript(null);
    setAiUpdatedAt({ email: null, phone_script: null, student_call: null });
    setAiErrorByTab({ email: null, phone_script: null, student_call: null });
    autoDraftedRef.current = { email: false, phone: false, prep: false };
    toast({ title: "Saved drafts cleared", description: "Regenerating from current incident notes." });
    window.setTimeout(() => {
      refreshFromCurrentNotes();
    }, 0);
  };

  const currentContent = activeTab === "email"
    ? effectiveEmail
    : activeTab === "phone_script"
      ? effectivePhoneScript
      : effectivePrepScript;

  const handleCopy = (text?: string) => {
    navigator.clipboard.writeText(text || currentContent);
    toast({ title: "Copied to clipboard" });
  };

  const handleCopyRich = async (html: string, text: string) => {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      toast({ title: "Copied with formatting!" });
    } catch {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied to clipboard" });
    }
  };

  const handleDownload = (text: string, name: string) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrintStudentGuide = () => {
    const printWindow = window.open("", "_blank", "width=600,height=800");
    if (!printWindow) return;
    const html = `<!DOCTYPE html><html><head><title>My Call Home Guide</title>
    <style>body{font-family:'Segoe UI',system-ui,sans-serif;padding:40px;max-width:540px;margin:0 auto;color:#1a1a1a;line-height:1.8;font-size:13pt}
    blockquote{margin:8px 0 8px 16px;padding:8px 12px;border-left:3px solid #ca8a04;background:#fefce8;font-style:italic}
    h2{color:#0D4436;border-bottom:2px solid #0D4436;padding-bottom:6px}h3{color:#0D4436;margin-top:16px}
    hr{border:none;border-top:1px solid #d1d5db;margin:16px 0}ul{padding-left:20px}li{margin:4px 0}</style></head>
    <body>${studentCallGuideHTML}</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const tabs = [
    { key: "email" as const, label: "Parent Email", icon: Mail },
    { key: "phone_script" as const, label: "Phone Script", icon: Phone },
    { key: "student_call" as const, label: "Student Calls Home", icon: User },
  ];

  return (
    <ContentCard>
      <h2 className="text-lg font-semibold text-bodhi-teal font-literata mb-1">
        Parent Communication
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Ready-to-use communication for the parent — choose your approach.
      </p>

      <div className="flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setIsEditing(false); setIsEditingPhone(false); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all",
                activeTab === tab.key
                  ? "bg-white text-bodhi-teal shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-3 rounded-md border border-bodhi-teal/20 bg-bodhi-teal/5 px-3 py-2 text-xs text-gray-700 space-y-1.5">
        <div className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700">
          Source: {activeSourceLabel}
        </div>
        <div className="font-medium text-gray-800">
          Notes-based draft first → then AI rewrites the communication using your incident notes.
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <button
            type="button"
            onClick={refreshFromCurrentNotes}
            className="underline decoration-dotted underline-offset-2 text-bodhi-teal"
            disabled={activeIsGenerating}
          >
            Refresh from current incident notes
          </button>
          <button
            type="button"
            onClick={clearSavedDraftsForIncident}
            className="underline decoration-dotted underline-offset-2 text-gray-600"
            disabled={activeIsGenerating}
          >
            Clear saved drafts for this incident
          </button>
        </div>
        {activeIsGenerating && (
          <div className="inline-flex items-center gap-1.5 text-bodhi-teal">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Generating AI communication from notes now...
          </div>
        )}
        {activeError && (
          <div className="text-red-700 flex flex-wrap items-center gap-2">
            <span>AI draft did not complete: {activeError}</span>
            <button
              type="button"
              onClick={retryActiveAiDraft}
              className="underline decoration-dotted underline-offset-2 text-bodhi-teal"
            >
              Retry AI rewrite
            </button>
          </div>
        )}
        {activeHasAiDraft && (
          <div className="flex items-center justify-between gap-2">
            <span>
              ✨ AI rewrite active for this tab.
              {activeUpdatedAt ? ` Updated ${new Date(activeUpdatedAt).toLocaleTimeString()}.` : ""}
            </span>
            <button
              type="button"
              className="underline decoration-dotted underline-offset-2 text-bodhi-teal"
              onClick={() =>
                setShowBaselineByTab((s) => ({
                  ...s,
                  [activeTab]: !s[activeTab],
                }))
              }
            >
              {showBaselineByTab[activeTab] ? "Show AI rewrite" : "Show notes-based draft"}
            </button>
          </div>
        )}
      </div>

      {/* Student Calls Home: two-document layout */}
      {activeTab === "student_call" && (
        <div className="space-y-4 mb-4">
          <div className="bg-bodhi-gold/10 border border-bodhi-gold/20 rounded-lg p-3 mb-2">
            <p className="text-xs text-gray-700">
              <strong>Two documents below:</strong> (1) Your prep guide for coaching the student before the call, and
              (2) a printable guide the student can hold during the call.
            </p>
          </div>

          {/* Teacher prep doc */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-bodhi-teal" />
              Your Prep Guide (admin/teacher only)
            </h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 max-h-[300px] overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">{effectivePrepScript}</pre>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={() => handleCopy(effectivePrepScript)} className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <GoogleDocExportButton
                plain={effectivePrepScript}
                docTitle={`Call Home Prep Guide - ${props.studentIdentifier || "Student"} - ${new Date().toLocaleDateString()}`}
                label="Google Doc"
                dialogTitle="Copy prep guide into a Google Doc"
                className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs"
              />
              <Button variant="outline" size="sm" onClick={() => handleDownload(effectivePrepScript, "teacher-prep-call-home")} className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateAIPrepScript()}
                disabled={isGeneratingPrep}
                className="text-xs text-bodhi-teal border-bodhi-teal/30 hover:bg-bodhi-teal/5"
              >
                {isGeneratingPrep ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Generate AI rewrite from notes
              </Button>
              {editedPrepScript && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditedPrepScript(null)}
                  className="text-xs text-gray-400"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Student-facing call guide */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Phone className="w-4 h-4 text-bodhi-gold" />
              Student's Call Guide (share with student)
            </h3>
            <div className="bg-white rounded-lg border-2 border-bodhi-gold/30 p-4 max-h-[300px] overflow-y-auto">
              <div
                className="prose prose-sm max-w-none text-gray-800 leading-relaxed [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-bodhi-teal [&_h2]:mt-0 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-700 [&_h3]:mt-3 [&_h3]:mb-1 [&_blockquote]:border-l-2 [&_blockquote]:border-bodhi-gold/40 [&_blockquote]:bg-amber-50/50 [&_blockquote]:pl-3 [&_blockquote]:py-1 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5"
                dangerouslySetInnerHTML={{ __html: studentCallGuideHTML }}
              />
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={() => handleCopyRich(studentCallGuideHTML, studentCallGuideText)} className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs">
                <Copy className="w-3 h-3 mr-1" /> Copy (Formatted)
              </Button>
              <GoogleDocExportButton
                html={studentCallGuideHTML}
                plain={studentCallGuideText}
                docTitle={`Student Call Home Guide - ${props.studentIdentifier || "Student"} - ${new Date().toLocaleDateString()}`}
                label="Copy & open Google Doc"
                dialogTitle="Copy student call guide into a Google Doc"
                className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs"
              />
              <Button variant="outline" size="sm" onClick={handlePrintStudentGuide} className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs">
                <Printer className="w-3 h-3 mr-1" /> Print for Student
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDownload(studentCallGuideText, "student-call-guide")} className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email and Phone Script tabs */}
      {activeTab !== "student_call" && (
        <>
          {activeTab === "email" && !isEditing && (
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setIsEditing(true); if (!editedEmail) setEditedEmail(parentEmail); }}
                className="text-xs"
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit Draft
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateAIEmail()}
                disabled={isGenerating}
                className="text-xs text-bodhi-teal border-bodhi-teal/30 hover:bg-bodhi-teal/5"
              >
                {isGenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Generate AI rewrite from notes
              </Button>
            </div>
          )}

          {activeTab === "phone_script" && !isEditingPhone && (
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditingPhone(true);
                  if (!editedPhoneScript) setEditedPhoneScript(phoneScript);
                }}
                className="text-xs"
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit Draft
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateAIPhoneScript()}
                disabled={isGeneratingPhone}
                className="text-xs text-bodhi-teal border-bodhi-teal/30 hover:bg-bodhi-teal/5"
              >
                {isGeneratingPhone ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Generate AI rewrite from notes
              </Button>
            </div>
          )}

          {isEditing && activeTab === "email" ? (
            <div className="mb-4">
              <Textarea
                value={editedEmail ?? parentEmail}
                onChange={(e) => setEditedEmail(e.target.value)}
                className="min-h-[300px] text-sm font-sans leading-relaxed"
                rows={12}
              />
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => setIsEditing(false)} className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 text-xs">
                  Done Editing
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditedEmail(null); setIsEditing(false); }} className="text-xs text-gray-400">
                  Reset to Default
                </Button>
              </div>
            </div>
          ) : isEditingPhone && activeTab === "phone_script" ? (
            <div className="mb-4">
              <Textarea
                value={editedPhoneScript ?? phoneScript}
                onChange={(e) => setEditedPhoneScript(e.target.value)}
                className="min-h-[300px] text-sm font-sans leading-relaxed"
                rows={14}
              />
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={() => setIsEditingPhone(false)}
                  className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 text-xs"
                >
                  Done Editing
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditedPhoneScript(null);
                    setIsEditingPhone(false);
                  }}
                  className="text-xs text-gray-400"
                >
                  Reset to Default
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4 max-h-[400px] overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">{currentContent}</pre>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button onClick={() => handleCopy()} variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-100">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            <GoogleDocExportButton
              plain={currentContent}
              docTitle={`${activeTab === "email" ? "Parent Email" : "Phone Script"} - ${props.studentIdentifier || "Student"} - ${new Date().toLocaleDateString()}`}
              label="Google Doc"
              dialogTitle={
                activeTab === "email"
                  ? "Copy parent email into a Google Doc"
                  : "Copy phone script into a Google Doc"
              }
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
            />
            <Button onClick={() => handleDownload(currentContent, activeTab === "email" ? "parent-email" : "phone-script")} variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-100">
              <Download className="w-3 h-3 mr-1" /> Download
            </Button>
            {activeTab === "email" && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="border-gray-300 text-gray-400 hover:bg-transparent cursor-not-allowed opacity-50"
                      >
                        <Mail className="w-3 h-3 mr-1" /> Open in Email Client
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Email client integration coming soon</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </>
      )}

      {/* Parent contact documentation */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <Label className="text-sm font-semibold text-gray-700 mb-2 block">Document parent contact</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <button
            type="button"
            onClick={() => props.onUpdateParentContactType("student_with_adult")}
            className={cn(
              "rounded-lg border-2 p-3 text-left transition-all text-sm",
              props.parentContactType === "student_with_adult"
                ? "border-bodhi-teal bg-bodhi-teal/10"
                : "border-gray-200 bg-white hover:border-gray-300"
            )}
          >
            <span className="font-medium">Student called with adult</span>
          </button>
          <button
            type="button"
            onClick={() => props.onUpdateParentContactType("adult_alone")}
            className={cn(
              "rounded-lg border-2 p-3 text-left transition-all text-sm",
              props.parentContactType === "adult_alone"
                ? "border-bodhi-teal bg-bodhi-teal/10"
                : "border-gray-200 bg-white hover:border-gray-300"
            )}
          >
            <span className="font-medium">Admin called alone</span>
          </button>
        </div>
        <Textarea
          placeholder="Notes from the parent call..."
          rows={2}
          className="text-sm"
          value={props.parentCallNotes}
          onChange={(e) => props.onUpdateParentCallNotes(e.target.value)}
        />
      </div>
    </ContentCard>
  );
};
